"use client";

import { useState } from "react";
import { usePathname, useRouter } from "next/navigation";
import Link from "next/link";
import { useUser, UserButton } from "@clerk/nextjs";
import GlobalSearch from "./GlobalSearch";
import NotificationCenter from "./NotificationCenter";
import UserProfileCustomPage from "./UserProfileCustomPage";
import DarkModeToggle from "./DarkModeToggle";
import { isClerkAdminUser } from "@/lib/clerkRoles";

export default function Header({ siteName, logoUrl }) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const pathname = usePathname();
  const router = useRouter();
  const { user } = useUser();
  const isAdminPage = pathname?.startsWith("/admin");
  const isDashboardPage = pathname?.startsWith("/dashboard");
  const isAdminUser = isClerkAdminUser(user);

  // Removed handleLoginClick - using Link components instead

  const handleNavClick = (e, section) => {
    // If on home page, prevent default and scroll to section
    if (pathname === "/" && !isAdminPage && !isDashboardPage) {
      e.preventDefault();
      const sectionId = section.replace("#", "");
      const element = document.getElementById(sectionId);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
    // If on other pages (like /waiver), let Link handle navigation normally
    // If on admin/dashboard page, Link will handle navigation to home page with hash
    setIsMenuOpen(false);
  };

  return (
    <header className="bg-black shadow-md sticky top-0 z-50">
      <nav className="container mx-auto px-4 py-4 relative">
        <div className="flex items-center justify-between">
          <Link
            href="/"
            className={`text-2xl font-bold text-cream hover:text-[#D4AF37] transition-colors flex items-center gap-2 ${
              isAdminPage || isDashboardPage ? "lg:ml-0 ml-12" : ""
            }`}
          >
            {logoUrl ? (
              <img
                src={logoUrl}
                alt={siteName || "Prodigy Combat Academy"}
                className="h-8 w-auto"
              />
            ) : null}
            <span className="text-[#D4AF37]">
              {siteName || "Prodigy Combat Academy"}
            </span>
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-4 md:space-x-8">
            {isAdminPage && <DarkModeToggle />}
            {/* Show UserButton on admin pages in navbar */}
            {isAdminPage && user && (
              <UserButton 
                afterSignOutUrl="/"
                userProfileMode="modal"
                showName={true}
                appearance={{
                  elements: {
                    userButtonText: "text-cream",
                    userButtonBox: "text-cream",
                  }
                }}
              >
                <UserButton.UserProfilePage 
                  label="Additional Info" 
                  url="additional-info"
                  labelIcon={
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                      <path d="M11.25 4.533A9.707 9.707 0 0 1 6 3a9.735 9.735 0 0 0-3.25.555.75.75 0 0 0-.5.707v14.25a.75.75 0 0 0 1 .707A8.237 8.237 0 0 1 6 18.75c1.995 0 3.823.707 5.25 1.886V4.533ZM12.75 20.636A8.214 8.214 0 0 1 18 18.75c.966 0 1.89.166 2.75.47a.75.75 0 0 0 1-.708V4.262a.75.75 0 0 0-.5-.707A9.735 9.735 0 0 0 18 3a9.707 9.707 0 0 0-5.25 1.533v16.103Z" />
                    </svg>
                  }
                >
                  <UserProfileCustomPage />
                </UserButton.UserProfilePage>
              </UserButton>
            )}
            {/* Search, notifications, and admin button are in sidebar on admin/dashboard pages */}
            {!isAdminPage && !isDashboardPage && (
              <>
                <Link
                  href="/#home"
                  onClick={(e) => handleNavClick(e, "#home")}
                  className="text-cream hover:text-[#D4AF37] transition-colors"
                >
                  Home
                </Link>
                <Link
                  href="/#about"
                  onClick={(e) => handleNavClick(e, "#about")}
                  className="text-cream hover:text-[#D4AF37] transition-colors"
                >
                  About
                </Link>
                <Link
                  href="/#classes"
                  onClick={(e) => handleNavClick(e, "#classes")}
                  className="text-cream hover:text-[#D4AF37] transition-colors"
                >
                  Classes
                </Link>
                <Link
                  href="/#schedule"
                  onClick={(e) => handleNavClick(e, "#schedule")}
                  className="text-cream hover:text-[#D4AF37] transition-colors"
                >
                  Schedule
                </Link>
                <Link
                  href="/waiver"
                  onClick={() => setIsMenuOpen(false)}
                  className="text-cream hover:text-[#D4AF37] transition-colors"
                >
                  Waiver
                </Link>
                {/* Show NotificationCenter to all logged-in users on home page */}
                {user && <NotificationCenter />}
                {user ? (
                  <Link
                    href={isAdminUser ? "/admin" : "/dashboard/subscriptions"}
                    onClick={() => setIsMenuOpen(false)}
                    className="bg-[#D4AF37] text-black px-6 py-2 rounded-full hover:bg-[#FFD700] transition-colors font-semibold"
                  >
                    {isAdminUser ? "Admin" : "Dashboard"}
                  </Link>
                ) : (
                  <>
                    <Link
                      href="/sign-up"
                      onClick={() => setIsMenuOpen(false)}
                      className="bg-[#D4AF37] text-black px-6 py-2 rounded-full hover:bg-[#FFD700] transition-colors font-semibold"
                    >
                      Get Started
                    </Link>
                    <Link
                      href="/sign-in"
                      onClick={() => setIsMenuOpen(false)}
                      className="bg-[#D4AF37] text-black px-6 py-2 rounded-full hover:bg-[#FFD700] transition-colors font-semibold"
                    >
                      Login
                    </Link>
                  </>
                )}
              </>
            )}
          </div>

          {/* Mobile: theme toggle + account (admin) */}
          {isAdminPage && (
            <div className="md:hidden flex items-center gap-2 flex-shrink-0">
              <DarkModeToggle className="border-cream/30 bg-black/40 text-cream hover:bg-white/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200" />
              {user && (
                <UserButton
                  afterSignOutUrl="/"
                  userProfileMode="modal"
                  showName={false}
                  appearance={{
                    elements: {
                      userButtonText: "text-cream",
                      userButtonBox: "text-cream",
                    },
                  }}
                >
                  <UserButton.UserProfilePage
                    label="Additional Info"
                    url="additional-info"
                    labelIcon={
                      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5">
                        <path d="M11.25 4.533A9.707 9.707 0 0 1 6 3a9.735 9.735 0 0 0-3.25.555.75.75 0 0 0-.5.707v14.25a.75.75 0 0 0 1 .707A8.237 8.237 0 0 1 6 18.75c1.995 0 3.823.707 5.25 1.886V4.533ZM12.75 20.636A8.214 8.214 0 0 1 18 18.75c.966 0 1.89.166 2.75.47a.75.75 0 0 0 1-.708V4.262a.75.75 0 0 0-.5-.707A9.735 9.735 0 0 0 18 3a9.707 9.707 0 0 0-5.25 1.533v16.103Z" />
                      </svg>
                    }
                  >
                    <UserProfileCustomPage />
                  </UserButton.UserProfilePage>
                </UserButton>
              )}
            </div>
          )}

          {/* Mobile Menu Button - Hide on admin/dashboard pages (sidebar handles it) */}
          {!isAdminPage && !isDashboardPage && (
            <button
              className="md:hidden text-gray-700"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label="Toggle menu"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                {isMenuOpen ? (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                ) : (
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 6h16M4 12h16M4 18h16"
                  />
                )}
              </svg>
            </button>
          )}
        </div>

        {/* Mobile Menu - Only show on home page (admin/dashboard use sidebar) */}
        {isMenuOpen && !isAdminPage && !isDashboardPage && (
          <div className="md:hidden mt-4 pb-4 space-y-3">
            {!isAdminPage && (
              <>
                <Link
                  href="/#home"
                  onClick={(e) => handleNavClick(e, "#home")}
                  className="block text-cream hover:text-[#D4AF37] transition-colors"
                >
                  Home
                </Link>
                <Link
                  href="/#about"
                  onClick={(e) => handleNavClick(e, "#about")}
                  className="block text-cream hover:text-[#D4AF37] transition-colors"
                >
                  About
                </Link>
                <Link
                  href="/#classes"
                  onClick={(e) => handleNavClick(e, "#classes")}
                  className="block text-cream hover:text-[#D4AF37] transition-colors"
                >
                  Classes
                </Link>
                <Link
                  href="/#schedule"
                  onClick={(e) => handleNavClick(e, "#schedule")}
                  className="block text-cream hover:text-[#D4AF37] transition-colors"
                >
                  Schedule
                </Link>
                <Link
                  href="/waiver"
                  onClick={() => setIsMenuOpen(false)}
                  className="block text-cream hover:text-[#D4AF37] transition-colors"
                >
                  Waiver
                </Link>
              </>
            )}
            <div className="flex items-center gap-3 pt-2 border-t border-cream/20">
              <span className="text-xs text-cream/80">Theme</span>
              <DarkModeToggle className="border-cream/30 bg-black/40 text-cream hover:bg-white/10 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200" />
            </div>
            {user ? (
              <Link
                href={isAdminUser ? "/admin" : "/dashboard/subscriptions"}
                onClick={() => setIsMenuOpen(false)}
                className="block bg-[#D4AF37] text-black px-6 py-2 rounded-full text-center hover:bg-[#FFD700] transition-colors font-semibold"
              >
                {isAdminUser ? "Admin" : "Dashboard"}
              </Link>
            ) : (
              <>
                <Link
                  href="/sign-up"
                  onClick={() => setIsMenuOpen(false)}
                  className="block bg-[#D4AF37] text-black px-6 py-2 rounded-full text-center hover:bg-[#FFD700] transition-colors font-semibold"
                >
                  Get Started
                </Link>
                <Link
                  href="/sign-in"
                  onClick={() => setIsMenuOpen(false)}
                  className="block bg-[#D4AF37] text-black px-6 py-2 rounded-full text-center hover:bg-[#FFD700] transition-colors font-semibold"
                >
                  Login
                </Link>
              </>
            )}
          </div>
        )}
      </nav>
    </header>
  );
}
